<template>
  <div class="w-full">
    <!-- Question text -->
    <div class="mb-6">
      <FimButton
        v-if="isOverlayAvailable"
        type="raw"
        no-padding
        html-type="button"
        :aria-label="$t('contact_lens_consultant.additional_info')"
        is-small
        class="flex items-baseline gap-2 !whitespace-normal text-left font-normal hover:text-shade-400"
        @click="showInfoOverlay"
      >
        <template #icon>
          <LazyIconFielmannQuestionBubble
            class="relative top-0.5 size-5 shrink-0"
          />
        </template>
        <h2 class="text-2xl font-medium">
          <div v-if="typeof question.question === 'string'">
            {{ question.question }}
          </div>
          <div v-else-if="question.question?.type === 'doc'">
            <RichTextRenderer :document="question.question" />
          </div>
        </h2>
      </FimButton>
      <h2 v-else class="text-2xl font-medium">
        <div v-if="typeof question.question === 'string'">
          {{ question.question }}
        </div>
        <div v-else-if="question.question?.type === 'doc'">
          <RichTextRenderer :document="question.question" />
        </div>
      </h2>
    </div>

    <!-- Answers -->
    <div class="mb-8">
      <div class="space-y-4">
        <div class="flex flex-col gap-4">
          <div
            v-for="answer in question.answers || []"
            :key="answer._uid"
            class="w-full rounded-lg border border-gray-300 hover:border-primary"
            :class="{
              'border-primary': isSelected(
                question.question_id,
                answer.answer_id,
              ),
            }"
          >
            <div class="flex flex-col">
              <RadioItem
                v-if="typeof payload[question.question_id] === 'string'"
                v-model="payload[question.question_id] as string"
                :value="answer.answer_id"
                :name="answer._uid"
                class="mr-2 p-4"
              >
                <template #label>
                  <span
                    v-if="typeof answer.answer === 'string'"
                    class="text-base"
                  >
                    {{ answer.answer }}
                  </span>
                  <div
                    v-else-if="answer.answer?.type === 'doc'"
                    class="text-base"
                  >
                    <RichTextRenderer :document="answer.answer" />
                  </div>
                </template>
              </RadioItem>
              <CheckboxItem
                v-else
                v-model="
                  (payload[question.question_id] as Record<string, boolean>)[
                    answer.answer_id
                  ]
                "
                :value="answer.answer_id"
                :name="answer._uid"
                class="mr-2 p-4"
              >
                <template #label>
                  <span
                    v-if="typeof answer.answer === 'string'"
                    class="text-base"
                  >
                    {{ answer.answer }}
                  </span>
                  <div
                    v-else-if="answer.answer?.type === 'doc'"
                    class="text-base"
                  >
                    <RichTextRenderer :document="answer.answer" />
                  </div>
                </template>
              </CheckboxItem>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Overlay
      v-if="isOverlayAvailable"
      portal-target="#info-overlay"
      :title="overlayTitle"
      :open="isOverlayOpen"
      layout="right"
      enter-from="right"
      @update:open="isOverlayOpen = false"
    >
      <div v-for="content in overlayContent" :key="content._uid">
        <component :is="content.component" :blok="content" />
      </div>
    </Overlay>
  </div>
</template>

<script setup lang="ts">
import type {
  SbQuestionairePayload,
  SbQuestionnaireStep,
} from '~/storyblok/types/storyblok'

const { question, payload, isSelected } = defineProps<{
  question: SbQuestionnaireStep
  payload: SbQuestionairePayload
  isSelected: (questionId: string, answerId: string) => boolean
}>()

const isOverlayOpen = ref<boolean>(false)
const overlayTitle = computed(() => question.overlayTitle)
const overlayContent = computed(() => question.overlayContent)
const isOverlayAvailable = computed(
  () => overlayTitle.value && overlayContent.value,
)

const showInfoOverlay = () => {
  isOverlayOpen.value = true
}
</script>
